

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AddressPopup = ({
  isOpen,
  onClose,
  addresses = [],
  selectedAddressObj,
  onAddressSelect,
  onAddressAdded,
  fetchAddresses
}) => {
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    doorNumber: "", addressLine: "", landMark: "", city: "", state: "", 
    pincode: "", name: "", phoneNumber: "", latitude: null, longitude: null, 
    category: "Home"
  });
  const [isLocating, setIsLocating] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const GOOGLE_MAPS_API_KEY = "AIzaSyDRcDLmtI8sk6pFB3w1o1N0RyPnRYO2HXQ";

  // Fetch addresses from API
  const fetchUserAddresses = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("authToken");
      
      if (!userId) {
        console.error("User ID not found");
        return;
      }

      const res = await axios.get(
        `https://backend.maamaas.com/subscription/api/user/get/addresses/${userId}`,
        {
          headers: token ? {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          } : {},
        }
      );

      if (res?.data) {
        setSavedAddresses(res.data);
        if (fetchAddresses) {
          await fetchAddresses();
        }
      } else {
        setSavedAddresses([]);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error?.response || error.message);
      toast.error("Failed to load addresses");
    }
  };

  // Load addresses when popup opens
  useEffect(() => {
    if (isOpen) {
      fetchUserAddresses();
    }
  }, [isOpen]);

  const extractAddressFields = (components = []) => {
    const get = (type) => components.find(c => c.types.includes(type))?.long_name || "";
    return {
      city: get("locality") || get("administrative_area_level_2"),
      state: get("administrative_area_level_1"),
      pincode: get("postal_code"),
    };
  };

  const moveMap = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
    setAddressForm(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    if (mapRef.current && mapRef.current.setCenter) {
      mapRef.current.setCenter({ lat, lng });
      mapRef.current.setZoom(16);
    }

    if (markerRef.current && markerRef.current.setPosition) {
      markerRef.current.setPosition({ lat, lng });
      markerRef.current.setVisible(true);
    }
  };

  const loadGoogleMapsScript = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve(window.google);
        return;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&v=weekly`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps loaded successfully");
        resolve(window.google);
      };
      script.onerror = (error) => {
        console.error("Google Maps script failed to load", error);
        setMapError(true);
        reject(error);
      };
      document.head.appendChild(script);
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const initGoogleMaps = async () => {
      try {
        await loadGoogleMapsScript();
        if (isMounted) {
          setGoogleMapsLoaded(true);
          setMapError(false);
        }
      } catch (error) {
        console.error("Failed to load Google Maps:", error);
        if (isMounted) {
          setMapError(true);
        }
      }
    };

    initGoogleMaps();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !googleMapsLoaded || mapInitialized) return;

    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 10;

    const initializeMap = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Waiting for Google Maps... retry ${retryCount}/${maxRetries}`);
          setTimeout(initializeMap, 500);
        }
        return;
      }

      const mapElement = document.getElementById("address-map");
      if (!mapElement) {
        console.error("Map element not found");
        return;
      }

      if (!isMounted) return;

      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              
              mapRef.current = new window.google.maps.Map(mapElement, {
                center: userLocation,
                zoom: 14,
                mapTypeControl: false,
                fullscreenControl: true,
                streetViewControl: false,
                zoomControl: true,
              });

              markerRef.current = new window.google.maps.Marker({
                map: mapRef.current,
                draggable: true,
                position: userLocation,
                animation: window.google.maps.Animation.DROP,
              });

              setAddressForm(prev => ({
                ...prev,
                latitude: userLocation.lat,
                longitude: userLocation.lng
              }));
              
              setLatitude(userLocation.lat);
              setLongitude(userLocation.lng);

              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: userLocation }, (results, status) => {
                if (status === "OK" && results[0]) {
                  const { city, state, pincode } = extractAddressFields(results[0].address_components);
                  setAddressForm(prev => ({
                    ...prev,
                    city,
                    state,
                    pincode
                  }));
                  if (searchInputRef.current) {
                    searchInputRef.current.value = results[0].formatted_address;
                  }
                }
              });
            },
            (error) => {
              console.log("Could not get user location, using default:", error);
              const defaultCenter = { lat: 20.5937, lng: 78.9629 };
              
              mapRef.current = new window.google.maps.Map(mapElement, {
                center: defaultCenter,
                zoom: 5,
                mapTypeControl: false,
                fullscreenControl: true,
                streetViewControl: false,
                zoomControl: true,
              });

              markerRef.current = new window.google.maps.Marker({
                map: mapRef.current,
                draggable: true,
                visible: false,
                animation: window.google.maps.Animation.DROP,
              });
            }
          );
        } else {
          const defaultCenter = { lat: 20.5937, lng: 78.9629 };
          
          mapRef.current = new window.google.maps.Map(mapElement, {
            center: defaultCenter,
            zoom: 5,
            mapTypeControl: false,
            fullscreenControl: true,
            streetViewControl: false,
            zoomControl: true,
          });

          markerRef.current = new window.google.maps.Marker({
            map: mapRef.current,
            draggable: true,
            visible: false,
            animation: window.google.maps.Animation.DROP,
          });
        }

        if (searchInputRef.current) {
          const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            types: ["geocode"],
            componentRestrictions: { country: "IN" }
          });

          autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (place && place.geometry && place.geometry.location) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              moveMap(lat, lng);

              if (place.address_components) {
                const { city, state, pincode } = extractAddressFields(place.address_components);
                setAddressForm(prev => ({
                  ...prev,
                  city,
                  state,
                  pincode,
                  latitude: lat,
                  longitude: lng
                }));
              }
            }
          });
        }

        if (markerRef.current) {
          markerRef.current.addListener("dragend", (e) => {
            if (isMounted) {
              const lat = e.latLng.lat();
              const lng = e.latLng.lng();
              setLatitude(lat);
              setLongitude(lng);
              setAddressForm(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng
              }));

              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === "OK" && results[0]) {
                  const { city, state, pincode } = extractAddressFields(results[0].address_components);
                  setAddressForm(prev => ({
                    ...prev,
                    city,
                    state,
                    pincode
                  }));
                  if (searchInputRef.current && !searchInputRef.current.value) {
                    searchInputRef.current.value = results[0].formatted_address;
                  }
                }
              });
            }
          });
        }

        setMapInitialized(true);
        console.log("Map initialized successfully");

      } catch (error) {
        console.error("Error initializing map:", error);
        setMapError(true);
      }
    };

    setTimeout(initializeMap, 200);

    return () => {
      isMounted = false;
      setMapInitialized(false);
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current = null;
      }
    };
  }, [isOpen, googleMapsLoaded]);

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        setLatitude(lat);
        setLongitude(lng);
        setAddressForm(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));

        if (mapRef.current && mapRef.current.setCenter) {
          mapRef.current.setCenter({ lat, lng });
          mapRef.current.setZoom(16);
        }

        if (markerRef.current) {
          markerRef.current.setPosition({ lat, lng });
          markerRef.current.setVisible(true);
        }

        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const address = results[0].formatted_address;
              const { city, state, pincode } = extractAddressFields(results[0].address_components);

              setAddressForm(prev => ({
                ...prev,
                city,
                state,
                pincode,
                latitude: lat,
                longitude: lng
              }));

              if (searchInputRef.current) {
                searchInputRef.current.value = address;
              }
              
              toast.success("Location detected successfully!");
            }
            setIsLocating(false);
          });
        } else {
          setIsLocating(false);
          toast.success("Location detected! Please enter address details manually.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Unable to fetch your location. ";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += "Please allow location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out.";
            break;
          default:
            errorMessage += "Please try again.";
        }
        toast.error(errorMessage);
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const submitNewAddress = async () => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!addressForm.latitude || !addressForm.longitude) {
      toast.error("Please select location on map or use current location");
      return;
    }

    if (!addressForm.category) {
      toast.error("Please select address category (Home/Office/Other)");
      return;
    }

    if (!addressForm.addressLine && !addressForm.doorNumber) {
      toast.error("Please enter at least door number or address line");
      return;
    }

    if (!addressForm.name) {
      toast.error("Please enter recipient name");
      return;
    }

    if (!addressForm.phoneNumber) {
      toast.error("Please enter phone number");
      return;
    }

    const addressParts = [
      addressForm.doorNumber,
      addressForm.addressLine,
      addressForm.landMark,
      addressForm.city,
      addressForm.state
    ].filter(Boolean);

    const addressString = addressParts.join(", ");

    const payload = {
      id: 0,
      userId: Number(userId),
      doorNumber: addressForm.doorNumber,
      addressLine: addressForm.addressLine,
      landMark: addressForm.landMark,
      city: addressForm.city,
      state: addressForm.state,
      pincode: Number(addressForm.pincode) || 0,
      name: addressForm.name,
      phoneNumber: addressForm.phoneNumber,
      latitude: Number(addressForm.latitude),
      longitude: Number(addressForm.longitude),
      address: addressString,
      category: addressForm.category
    };

    try {
      const response = await axios.post(
        "https://backend.maamaas.com/subscription/api/user/location/add", 
        payload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`, 
            "Content-Type": "application/json" 
          } 
        }
      );
      
      console.log("Save address response:", response.data);
      toast.success("Address saved successfully");
      
      await fetchUserAddresses();
      
      setShowAddAddressForm(false);
      
      const newAddress = {
        id: response.data.id || response.data,
        address: addressString,
        addressLine: addressForm.addressLine,
        doorNumber: addressForm.doorNumber,
        landMark: addressForm.landMark,
        city: addressForm.city,
        state: addressForm.state,
        pincode: addressForm.pincode,
        name: addressForm.name,
        phoneNumber: addressForm.phoneNumber,
        latitude: addressForm.latitude,
        longitude: addressForm.longitude,
        category: addressForm.category
      };
      
      if (onAddressAdded) {
        onAddressAdded(newAddress);
      }
      
      setAddressForm({
        doorNumber: "", addressLine: "", landMark: "", city: "", state: "", 
        pincode: "", name: "", phoneNumber: "", latitude: null, longitude: null, 
        category: "Home"
      });
      
      setLatitude(null);
      setLongitude(null);
      
    } catch (error) {
      console.error("Failed to add address", error);
      toast.error(error.response?.data?.message || "Failed to save address");
    }
  };

  const getFilteredAddresses = () => {
    if (selectedCategory === "All") {
      return savedAddresses;
    }
    return savedAddresses.filter(addr => addr.category === selectedCategory);
  };

  const getAddressesByCategory = () => {
    const categories = {
      Home: [],
      Office: [],
      Other: []
    };
    
    savedAddresses.forEach(addr => {
      const category = addr.category || "Home";
      if (categories[category]) {
        categories[category].push(addr);
      } else {
        categories.Other.push(addr);
      }
    });
    
    return categories;
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Home': return '🏠';
      case 'Office': return '💼';
      case 'Other': return '📍';
      default: return '🏠';
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Home': return '#ee6521';
      case 'Office': return '#3b82f6';
      case 'Other': return '#8b5cf6';
      default: return '#ee6521';
    }
  };

  if (!isOpen) return null;

  const filteredAddresses = getFilteredAddresses();
  const addressesByCategory = getAddressesByCategory();
  const categories = ["All", "Home", "Office", "Other"];

  // Styles object
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    },
    container: {
      backgroundColor: '#fff',
      borderRadius: '20px',
      width: '90%',
      maxWidth: '550px',
      maxHeight: '90vh',
      overflowY: 'auto',
      padding: '24px',
      position: 'relative',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      // Custom scrollbar styles
      scrollbarWidth: 'thin',
      scrollbarColor: '#ee6521 #f1f1f1',
    },
    closeBtn: {
      position: 'absolute',
      right: '20px',
      top: '20px',
      fontSize: '24px',
      cursor: 'pointer',
      zIndex: 1,
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      backgroundColor: '#f3f4f6',
      transition: 'all 0.2s'
    },
    title: {
      margin: '0 0 24px 0',
      color: '#ee6521',
      fontSize: '24px',
      fontWeight: 'bold'
    },
    mapSection: {
      marginBottom: '20px'
    },
    searchContainer: {
      position: "relative",
      width: "100%",
      marginBottom: "15px"
    },
    searchInput: {
      width: "100%",
      padding: "12px 40px 12px 16px",
      fontSize: "14px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    searchIcon: {
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      cursor: "pointer",
      fontSize: '18px'
    },
    mapContainer: {
      width: '100%',
      height: '250px',
      borderRadius: '12px',
      marginBottom: '15px',
      overflow: 'hidden'
    },
    mapErrorBox: {
      width: '100%',
      height: '250px',
      borderRadius: '12px',
      marginBottom: '15px',
      backgroundColor: '#f9fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '10px',
      border: '2px dashed #e5e7eb'
    },
    locateBtn: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#ee6521',
      color: '#fff',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    addAddressBtn: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '20px'
    },
    addBtn: {
      padding: "10px 24px",
      backgroundColor: "#22c55e",
      color: "#fff",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s'
    },
    formContainer: {
      backgroundColor: '#f9fafb',
      padding: '20px',
      borderRadius: '16px',
      marginBottom: '20px',
      border: '2px solid #e5e7eb'
    },
    formTitle: {
      margin: '0 0 16px 0',
      color: '#374151',
      fontSize: '16px',
      fontWeight: '600'
    },
    categoryGroup: {
      display: 'flex',
      gap: '12px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    categoryBtn: {
      flex: 1,
      padding: '10px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s',
      minWidth: '80px'
    },
    input: {
      width: '100%',
      padding: '12px',
      marginBottom: '12px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box'
    },
    rowInputs: {
      display: 'flex',
      gap: '12px',
      marginBottom: '12px',
      flexWrap: 'wrap'
    },
    halfInput: {
      flex: 1,
      padding: '12px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '14px',
      outline: 'none',
      minWidth: '120px'
    },
    saveBtn: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#22c55e',
      color: '#fff',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s',
      marginTop: '4px'
    },
    addressesHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    headerTitle: {
      margin: 0,
      color: '#374151',
      fontSize: '16px',
      fontWeight: '600'
    },
    categoryFilters: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    filterBtn: {
      padding: '6px 12px',
      borderRadius: '20px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'all 0.2s',
      border: 'none'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      backgroundColor: '#f9fafb',
      borderRadius: '12px',
      border: '2px dashed #e5e7eb'
    },
    emptyEmoji: {
      fontSize: '48px',
      display: 'block',
      marginBottom: '12px'
    },
    emptyText: {
      color: '#6b7280',
      margin: 0,
      fontSize: '14px'
    },
    categorySection: {
      marginBottom: '20px'
    },
    categoryHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: `2px solid`
    },
    categoryTitle: {
      margin: 0,
      fontSize: '14px',
      fontWeight: '600'
    },
    categoryCount: {
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px'
    },
    // NEW: Grid layout for addresses on mobile
    addressGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '12px'
    },
    addressCard: {
      padding: '14px',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '2px solid',
      backgroundColor: '#fff'
    },
    addressHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '8px'
    },
    addressIcon: {
      fontSize: '20px'
    },
    categoryBadge: {
      flex: 1,
      fontSize: '14px',
      fontWeight: 'bold'
    },
    selectedBadge: {
      backgroundColor: '#22c55e',
      color: '#fff',
      fontSize: '11px',
      padding: '2px 8px',
      borderRadius: '20px'
    },
    addressText: {
      margin: '0 0 8px 30px',
      fontSize: '13px',
      color: '#6b7280',
      lineHeight: '1.5',
      wordBreak: 'break-word'
    },
    addressDetails: {
      marginLeft: '30px',
      display: 'flex',
      gap: '16px',
      fontSize: '11px',
      color: '#9ca3af',
      flexWrap: 'wrap'
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        <style>
          {`
            /* Custom scrollbar for webkit browsers */
            .address-popup-scroll::-webkit-scrollbar {
              width: 6px;
            }
            .address-popup-scroll::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 10px;
            }
            .address-popup-scroll::-webkit-scrollbar-thumb {
              background: #ee6521;
              border-radius: 10px;
            }
            .address-popup-scroll::-webkit-scrollbar-thumb:hover {
              background: #e05a1a;
            }
            
            /* Hide scrollbar for mobile but keep functionality */
            @media (max-width: 768px) {
              .address-popup-scroll::-webkit-scrollbar {
                width: 0px;
                background: transparent;
              }
              .address-popup-scroll {
                scrollbar-width: none;
              }
            }
          `}
        </style>
        
        <div 
          className="address-popup-scroll"
          style={{ 
            maxHeight: 'calc(90vh - 48px)', 
            overflowY: 'auto',
            scrollbarWidth: 'none',
            paddingRight: '4px'
          }}
        >
          <span
            onClick={() => {
              onClose();
              setShowAddAddressForm(false);
              setSelectedCategory("All");
              setAddressForm({
                doorNumber: "", addressLine: "", landMark: "", city: "", state: "", 
                pincode: "", name: "", phoneNumber: "", latitude: null, longitude: null, 
                category: "Home"
              });
            }}
            style={styles.closeBtn}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          >
            ✖
          </span>
          
          <h3 style={styles.title}>
            Select Delivery Address
          </h3>

          {/* Map Section */}
          <div style={styles.mapSection}>
            <div style={styles.searchContainer}>
              <input
                type="text"
                ref={searchInputRef}
                placeholder="Search location..."
                style={styles.searchInput}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <span
                onClick={() => {
                  const query = searchInputRef.current?.value;
                  if (query && window.google?.maps) {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: query }, (results, status) => {
                      if (status === "OK" && results[0]) {
                        const location = results[0].geometry.location;
                        moveMap(location.lat(), location.lng());
                      } else {
                        toast.error("Location not found");
                      }
                    });
                  }
                }}
                style={styles.searchIcon}
              >
                🔍
              </span>
            </div>

            {mapError ? (
              <div style={styles.mapErrorBox}>
                <span style={{ fontSize: '48px' }}>🗺️</span>
                <p style={{ color: '#6b7280', textAlign: 'center', margin: 0, fontSize: '14px' }}>
                  Map loading failed. Please use search or current location.
                </p>
              </div>
            ) : (
              <div id="address-map" style={styles.mapContainer}></div>
            )}

            <button
              onClick={detectCurrentLocation}
              disabled={isLocating}
              style={{
                ...styles.locateBtn,
                opacity: isLocating ? 0.7 : 1
              }}
              onMouseEnter={(e) => !isLocating && (e.currentTarget.style.backgroundColor = '#e05a1a')}
              onMouseLeave={(e) => !isLocating && (e.currentTarget.style.backgroundColor = '#ee6521')}
            >
              {isLocating ? "Locating..." : "📍 Use My Current Location"}
            </button>
          </div>

          {/* Add New Address Button */}
          <div style={styles.addAddressBtn}>
            <button
              onClick={() => setShowAddAddressForm(!showAddAddressForm)}
              style={styles.addBtn}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
            >
              <span style={{ fontSize: '18px' }}>+</span> Add New Address
            </button>
          </div>

          {/* Add Address Form */}
          {showAddAddressForm && (
            <div style={styles.formContainer}>
              <h4 style={styles.formTitle}>
                Add New Address
              </h4>
              
              <div style={styles.categoryGroup}>
                {['Home', 'Office', 'Other'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setAddressForm({ ...addressForm, category: cat })}
                    style={{
                      ...styles.categoryBtn,
                      backgroundColor: addressForm.category === cat ? getCategoryColor(cat) : '#fff',
                      color: addressForm.category === cat ? '#fff' : '#374151',
                      border: `2px solid ${addressForm.category === cat ? getCategoryColor(cat) : '#e5e7eb'}`
                    }}
                  >
                    <span>{getCategoryIcon(cat)}</span> {cat}
                  </button>
                ))}
              </div>
              
              <input
                type="text"
                placeholder="Door Number"
                value={addressForm.doorNumber}
                onChange={(e) => setAddressForm({ ...addressForm, doorNumber: e.target.value })}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <input
                type="text"
                placeholder="Address Line *"
                value={addressForm.addressLine}
                onChange={(e) => setAddressForm({ ...addressForm, addressLine: e.target.value })}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <input
                type="text"
                placeholder="Landmark"
                value={addressForm.landMark}
                onChange={(e) => setAddressForm({ ...addressForm, landMark: e.target.value })}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <div style={styles.rowInputs}>
                <input
                  type="text"
                  placeholder="City"
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  style={styles.halfInput}
                  onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <input
                  type="text"
                  placeholder="State"
                  value={addressForm.state}
                  onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                  style={styles.halfInput}
                  onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <input
                type="number"
                placeholder="Pincode"
                value={addressForm.pincode}
                onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <input
                type="text"
                placeholder="Recipient Name *"
                value={addressForm.name}
                onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <input
                type="tel"
                placeholder="Phone Number *"
                value={addressForm.phoneNumber}
                onChange={(e) => setAddressForm({ ...addressForm, phoneNumber: e.target.value })}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#ee6521'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                onClick={submitNewAddress}
                style={styles.saveBtn}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
              >
                Save Address
              </button>
            </div>
          )}

          {/* Saved Addresses Section - Now with Grid Layout on Mobile */}
          <div>
            <div style={styles.addressesHeader}>
              <h4 style={styles.headerTitle}>
                Saved Addresses ({savedAddresses.length})
              </h4>
              <div style={styles.categoryFilters}>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      ...styles.filterBtn,
                      backgroundColor: selectedCategory === cat ? '#ee6521' : '#f3f4f6',
                      color: selectedCategory === cat ? '#fff' : '#6b7280'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {savedAddresses.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyEmoji}>📦</span>
                <p style={styles.emptyText}>
                  No saved addresses. Click "Add New Address" to add one.
                </p>
              </div>
            ) : selectedCategory === "All" ? (
              // Display grouped by category with responsive grid
              Object.entries(addressesByCategory).map(([category, categoryAddresses]) => 
                categoryAddresses.length > 0 && (
                  <div key={category} style={styles.categorySection}>
                    <div style={{ ...styles.categoryHeader, borderBottomColor: getCategoryColor(category) }}>
                      <span style={styles.addressIcon}>{getCategoryIcon(category)}</span>
                      <h5 style={{ ...styles.categoryTitle, color: getCategoryColor(category) }}>
                        {category}
                      </h5>
                      <span style={styles.categoryCount}>
                        {categoryAddresses.length}
                      </span>
                    </div>
                    <div style={styles.addressGrid}>
                      {categoryAddresses.map((a, i) => (
                        <div
                          key={a.id || i}
                          onClick={() => onAddressSelect(a)}
                          style={{
                            ...styles.addressCard,
                            borderColor: selectedAddressObj?.id === a.id ? getCategoryColor(category) : '#e5e7eb',
                            backgroundColor: selectedAddressObj?.id === a.id ? '#fff8f5' : '#fff',
                            transform: selectedAddressObj?.id === a.id ? 'scale(1.01)' : 'scale(1)'
                          }}
                          onMouseEnter={(e) => !selectedAddressObj?.id === a.id && (e.currentTarget.style.borderColor = getCategoryColor(category))}
                          onMouseLeave={(e) => !selectedAddressObj?.id === a.id && (e.currentTarget.style.borderColor = '#e5e7eb')}
                        >
                          <div style={styles.addressHeader}>
                            <span style={styles.addressIcon}>{getCategoryIcon(category)}</span>
                            <b style={{ ...styles.categoryBadge, color: getCategoryColor(category) }}>
                              {category}
                            </b>
                            {selectedAddressObj?.id === a.id && (
                              <span style={styles.selectedBadge}>
                                ✓ Selected
                              </span>
                            )}
                          </div>
                          <p style={styles.addressText}>
                            {a.address || `${a.doorNumber ? a.doorNumber + ', ' : ''}${a.addressLine || ''} ${a.landMark ? ', ' + a.landMark : ''} ${a.city ? ', ' + a.city : ''} ${a.state ? ', ' + a.state : ''}`}
                          </p>
                          <div style={styles.addressDetails}>
                            <span>👤 {a.name || 'Not specified'}</span>
                            <span>📞 {a.phoneNumber || 'Not specified'}</span>
                            {a.pincode && <span>📮 {a.pincode}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )
            ) : (
              // Display filtered addresses with responsive grid
              <div style={styles.addressGrid}>
                {filteredAddresses.map((a, i) => {
                  const category = a.category || "Home";
                  return (
                    <div
                      key={a.id || i}
                      onClick={() => onAddressSelect(a)}
                      style={{
                        ...styles.addressCard,
                        borderColor: selectedAddressObj?.id === a.id ? getCategoryColor(category) : '#e5e7eb',
                        backgroundColor: selectedAddressObj?.id === a.id ? '#fff8f5' : '#fff',
                        transform: selectedAddressObj?.id === a.id ? 'scale(1.01)' : 'scale(1)'
                      }}
                      onMouseEnter={(e) => !selectedAddressObj?.id === a.id && (e.currentTarget.style.borderColor = getCategoryColor(category))}
                      onMouseLeave={(e) => !selectedAddressObj?.id === a.id && (e.currentTarget.style.borderColor = '#e5e7eb')}
                    >
                      <div style={styles.addressHeader}>
                        <span style={styles.addressIcon}>{getCategoryIcon(category)}</span>
                        <b style={{ ...styles.categoryBadge, color: getCategoryColor(category) }}>
                          {category}
                        </b>
                        {selectedAddressObj?.id === a.id && (
                          <span style={styles.selectedBadge}>
                            ✓ Selected
                          </span>
                        )}
                      </div>
                      <p style={styles.addressText}>
                        {a.address || `${a.doorNumber ? a.doorNumber + ', ' : ''}${a.addressLine || ''} ${a.landMark ? ', ' + a.landMark : ''} ${a.city ? ', ' + a.city : ''} ${a.state ? ', ' + a.state : ''}`}
                      </p>
                      <div style={styles.addressDetails}>
                        <span>👤 {a.name || 'Not specified'}</span>
                        <span>📞 {a.phoneNumber || 'Not specified'}</span>
                        {a.pincode && <span>📮 {a.pincode}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressPopup;